import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Tooltip, Modal, message, Space } from "antd";
import { insertCRFDataIntoWord, insertCRFDataIntoWordAtPage } from "../../CRF";
import { Frontend_LOCAL_PROXY } from "../../../../constant";
import { EyeOutlined, FileWordOutlined } from "@ant-design/icons";
import UploadSourceDocument from "./UploadSourceDocument";
import { CRFContext } from "./CRFContext";
import {
  deleteProtocol,
  digitizeProtocol,
  generateCRF,
  generateReviewTable,
  getAllLLMResponse,
  getAllProtocolVersion,
  getDigitizationPercent,
  getSpecificVersion,
  updateSoaTable,
  uploadProtocol,
} from "../../../services/uploadService";
import { useAuth } from "../../contexts/AuthContext";
import customMessage from "../../../customMessage";

const CRF = ({ onBack }) => {
  const frontEndUrl = Frontend_LOCAL_PROXY;
  const [fileList, setFileList] = useState([]);
  const [protocolId, setProtocolId] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [tip, setTip] = useState("Loading, please wait...");
  const [loading, setLoading] = useState(false);
  const [isFileDigitized, setIsFileDigitized] = useState(false);
  const [progress, setProgress] = useState(null);
  const [templateProgress, setTemplateProgress] = useState(null);
  const [templateProgressCompleted, setTemplateProgressCompleted] = useState(null);
  const [totalTemplate, setTotalTemplate] = useState(0);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [reviewTableCache, setReviewTableCache] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [loadingVersion, setLoadingVersion] = useState(false);

  const { user } = useAuth();

  const pollIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  // A CRF row is uniquely identified by its form + folder (epoch). Sanitize the
  // generated rows before they reach the grouped table:
  //  - Drop blank-folder rows: a form with no folder selected should not surface
  //    as empty "-" rows (e.g. "Weight Test (3)" with three blank folders).
  //  - Dedupe by form+folder, keeping the first seen, so a form/folder never
  //    appears twice — the backend can return a form_id × folder cross-product
  //    (which otherwise showed as "weight ks1 (4)" with each folder listed twice).
  // Form names can carry a trailing note after "~~" (e.g. "Informed consent ~~ [Note]").
  // The same form's already-generated rows and its newly-generated rows can differ by
  // that note, which would split one form into two table groups. Normalize to the base
  // name so a form always groups as ONE row.
  const baseForm = (name) => (name ?? "").split("~~")[0].trim();
  const sanitizeRows = (rows) => {
    const seen = new Set();
    const out = [];
    for (const row of rows || []) {
      const folder = (row?.epoch ?? "").trim();
      if (!folder) continue; // skip rows with no folder
      const normalizedForm = baseForm(row?.form);
      const key = `${normalizedForm}||${folder}`; // dedupe by base form + folder
      if (seen.has(key)) continue;
      seen.add(key);
      // Overwrite `form` with the base name so identical forms share one group.
      out.push({ ...row, form: normalizedForm });
    }
    return out;
  };

  const abortControllerHandle = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // cancels the request
    }
  };

  const clearIntervalHandle = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
  };

  const pollDigitizationProgress = (protocol_id, onComplete) => {
    // clear old interval first
    clearIntervalHandle();

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await getDigitizationPercent({ protocol_id });
        const percent = parseInt(res.digitization_percent, 10) || 0;
        setProgress(percent);
        setTip(res.message);

        if (percent == 100) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          onComplete?.();
        }
      } catch (err) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (err?.response?.status === 504) {
          customMessage.error("Server timed out. Please try again.");
        } else {
          customMessage.error("Failed to fetch digitization progress.");
        }
        setProgress(null);
        setLoading(false);
        setProtocolId(null);
        setIsFileDigitized(false);
      }
    }, 6000);
  };

  const handleBase64Upload = async (base64File) => {
    const file = fileList[0];

    const payload = {
      user_id: user?.username,
      file_b64: base64File,
      file_name: file.name,
    };

    try {
      const response = await uploadProtocol(payload);
      const { protocol_id, file_path } = response;

      const digitizePayload = {
        user_id: user?.username,
        protocol_id: protocol_id,
        file_path: file_path,
      };
      message.success("File uploaded successfully!");
      setTip("File is being digitized...");
      // create a fresh controller
      abortControllerRef.current = new AbortController();

      digitizeProtocol(digitizePayload, abortControllerRef.current.signal)
        .then(() => {
          message.success("File digitized successfully!");
          // setIsFileDigitized(true);
          // setLoading(false);
          // setProgress(null);
        })
        .catch((err) => {
          if (err?.response?.status === 504) {
            customMessage.error("Server timed out. Please try again.");
          } else {
            customMessage.error("Digitization Failed.");
          }
          setLoading(false);
          setProgress(null);
          setProtocolId(null);
          setIsFileDigitized(false);
          // stop polling if digitization fails
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        });
      setLoading(true);
      pollDigitizationProgress(protocol_id, () => {
        setIsFileDigitized(true);
        setLoading(false);
        setProgress(null);
      });
      setProtocolId(protocol_id);
    } catch (error) {
      customMessage.error("Upload failed.");
      setLoading(false);
    }
  };

  const processSelectedTemplates = async (
    templates,
    setLoading,
    setDataSource,
    setProgress, // new
    setCompleted // new
  ) => {
    const BATCH_SIZE = 5;
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    setLoading(true);
    setTemplateLoading(true);
    setProgress(0);
    setCompleted(0);

    // Optimized API contract: one generate-crf call per FILE (form_name) instead
    // of one per folder. The incoming `templates` list is one entry per folder,
    // so group it by file and send all that file's folders as an array.
    const groupsMap = new Map();
    for (const tpl of templates) {
      const key = tpl.form_name;
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          form_id: tpl.form_id || [],
          form_name: tpl.form_name,
          source: tpl.source,
          protocol_id: tpl.protocol_id ?? protocolId,
          folder_name: [], // array of folder strings, index-aligned with review_id
          review_id: [], // parallel to folder_name
        });
      }
      const group = groupsMap.get(key);
      // Keep folder_name index-aligned with review_id, including empty strings,
      // e.g. folder_name: ["", "abc", "xyz"] / review_id: ["id0", "id1", "id2"].
      group.folder_name.push(tpl.folder_name ?? "");
      group.review_id.push(tpl.review_id);
    }
    const groups = Array.from(groupsMap.values());

    // Progress is counted in FILES (one per generate-crf call), so the top shows
    // "X/<file count>" rather than the folder count.
    const totalFiles = groups.length;
    setTotalTemplate(totalFiles);
    let done = 0;

    for (let i = 0; i < groups.length; i += BATCH_SIZE) {
      const batch = groups.slice(i, i + BATCH_SIZE);

      try {
        // One request per file; files within a batch run in parallel.
        await Promise.all(
          batch.map(async (group) => {
            try {
              const response = await generateCRF(group);

              // Service flattens the array-of-arrays, so `result` is a flat list
              // of form objects (one per folder of this file). Append in one go.
              if (Array.isArray(response?.result) && response.result.length > 0) {
                // Sanitize across already-present rows + this file's new rows:
                // drop blank-folder rows and dedupe by form/folder.
                setDataSource((prev) => sanitizeRows([...prev, ...response.result]));
              }
              setLoading(false);
            } catch (err) {
              console.error("Error processing file:", group.form_name, err);
            } finally {
              // Advance by one per file (per generate-crf call).
              done += 1;
              setCompleted(done);
              setProgress(Math.round((done / totalFiles) * 100));
            }
          })
        );
      } catch (err) {
        console.error(`Error in batch starting at index ${i}:`, err);
      }
      // Optional delay between batches
      await delay(1000);
    }

    setLoading(false);
    setTemplateLoading(false);
  };

  const onClickGenerate = async () => {
    setLoading(true);
    setTip("Review table is being generated...");
    // Create a fresh abort controller
    abortControllerRef.current = new AbortController();

    try {
      let result_json;

      if (reviewTableCache && reviewTableCache.protocolId === protocolId) {
        // Use cached data
        result_json = reviewTableCache.result_json;
      } else {
        // Call API if no cache or protocolId changed
        const response = await generateReviewTable({ protocol_id: protocolId }, abortControllerRef.current.signal);
        result_json = response?.result_json || [];
        // console.log("Data from taskpane to popup", result_json);
        if (!response?.result_json) throw new Error("Empty result_json");
        // Save to cache
        setReviewTableCache({ protocolId, result_json });
      }

      if (Office.context.ui) {
        Office.context.ui.displayDialogAsync(
          `${frontEndUrl}/CRFAssessmentsDialog.html`,
          { height: 100, width: 100 },
          (result) => {
            const dialog = result.value;
            dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args) => {
              const data = JSON.parse(args.message);
              if (data.action === "ready") {
                setTimeout(() => {
                  dialog.messageChild(JSON.stringify({ action: "init", payload: { result_json, protocolId, protocolFileName: fileList?.[0]?.name } }));
                }, 100);
              }
              if (data.action === "close") {
                const processed = data.payload || {};
                // NOTE: do NOT close the dialog yet — keep the assessment page open
                // (it shows its own loader) until update_soa completes, so the user
                // can't reach/close the generation page while update_soa is running.
                setLoading(true);
                // Clear any previous generation so the CRF page isn't visible while
                // update_soa is still in flight — show the normal loading state until
                // the response comes back and generation repopulates dataSource.
                setDataSource([]);
                setTip("CRF form fields is being generated...");
                console.log("Data from Popup", processed);

                // Save checkbox/folder state to the backend for new protocols,
                // matching what onClickSpecificVersion does for historic data.
                if (processed.soa_addititon?.length > 0) {
                  try {
                    await updateSoaTable({
                      soa_addititon: processed.soa_addititon,
                      protocol_id: processed.protocol_id || protocolId,
                    });
                  } catch (err) {
                    console.error("Failed to persist SOA checkbox state:", err);
                  }
                }

                // update_soa is done → now leave the assessment page for generation.
                dialog.close();

                const templates = processed.templates || processed;
                processSelectedTemplates(
                  templates,
                  setLoading,
                  setDataSource,
                  setTemplateProgress,
                  setTemplateProgressCompleted
                );
              }
            });
          }
        );
      }
    } catch (error) {
      if (error.name === "AbortError") {
        customMessage.warning("Generate request cancelled.");
      } else {
        customMessage.error("Generate Review table failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onClickSpecificVersion = async (item) => {
    const protocolId = item.protocol_id
    const protocolFileName = item.protocol
    setFileList([{ name: protocolFileName }])
    setLoading(true);
    setTip("Review table is being generated...");
    // Create a fresh abort controller
    abortControllerRef.current = new AbortController();

    try {
      let result_json;

      const response = await getSpecificVersion({ protocol_id: protocolId }, abortControllerRef.current.signal);
      result_json = response?.result_json || [];
      // console.log("Data from taskpane to popup", result_json);
      if (!response?.result_json) throw new Error("Empty result_json");
      // Save to cache
      setReviewTableCache({ protocolId, result_json });

      if (Office.context.ui) {
        Office.context.ui.displayDialogAsync(
          `${frontEndUrl}/CRFAssessmentsDialog.html`,
          { height: 100, width: 100 },
          (result) => {
            const dialog = result.value;
            dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args) => {
              const data = JSON.parse(args.message);
              if (data.action === "ready") {
                setTimeout(() => {
                  dialog.messageChild(JSON.stringify({ action: "init", payload: { result_json, protocolId, existingVersion: true, protocolFileName } }));
                }, 100);
              }
              if (data.action === "close") {
                const processed = data.payload || [];
                // NOTE: do NOT close the dialog yet — keep the assessment page open
                // (it shows its own loader) until update_soa completes, so the user
                // can't reach/close the generation page while update_soa is running.
                setLoading(true);
                // Clear any previous generation so the CRF page isn't visible while
                // update_soa is still in flight — show the normal loading state until
                // the response comes back and repopulates dataSource below.
                setDataSource([]);
                setTip("CRF form fields is being generated...");
                console.log("Data from Popup", processed);
                // `deleted_forms` is a FRONTEND-ONLY hint (carried over the dialog
                // message) used to filter the next screen — the backend hasn't
                // changed, so strip it out before the updateSoaTable call.
                const { deleted_forms: deletedFormsList = [], ...soaPayload } = processed || {};
                // Run update_soa while the dialog is still open, then close it
                // (finally → always closes, even if the call fails) → generation page.
                try {
                  await updateSoaTable(soaPayload)
                } finally {
                  dialog.close();
                }
                const { remaining_forms, generated_forms } = await getAllLLMResponse({ protocol_id: protocolId })

                // Forms the user soft-deleted (grayed out) in the assessment table
                // must not reappear in the next screen. getAllLLMResponse returns
                // previously generated forms regardless of is_deleted, so filter
                // them out here. Match on the base form name (part before "~~").
                const baseFormName = (name) => (name || "").split("~~")[0].trim();
                const deletedFormSet = new Set(deletedFormsList.map(baseFormName));
                const isDeletedForm = (name) => deletedFormSet.has(baseFormName(name));

                const payload = remaining_forms
                  .filter((row) => !isDeletedForm(row?.form_name))
                  .map((row) => {
                    const selectedTpl = (row.template || []).find(
                      (tpl) => tpl.selected === true
                    );

                    if (!selectedTpl) return null;

                    return {
                      form_id: selectedTpl?.form_id || [],
                      form_name: row?.form_name,
                      folder_name: row?.folder_name,
                      source: row?.source,
                      review_id: row?.review_id,
                      protocol_id: protocolId
                    };
                  })
                  .filter(Boolean);

                // Also directly generate CRFs for newly added/checked rows (soa_addititon).
                // getAllLLMResponse may not return these with selected=true since they were
                // just added, so we feed them directly into processSelectedTemplates.
                // The full `processed` payload (including checkbox:false rows) was
                // already sent to updateSoaTable above so the backend can deselect
                // unticked folders. For GENERATION we only want the ticked ones:
                // skip checkbox === false, and require a form_id.
                const soaRows = (processed?.soa_addititon || [])
                  .filter((row) => row.checkbox !== false)
                  .filter((row) => Array.isArray(row.form_id) && row.form_id.length > 0)
                  .map((row) => ({ ...row, protocol_id: protocolId }));

                const result = generated_forms
                  .flat()
                  .filter((row) => !isDeletedForm(row?.form));

                // Only generate folders that aren't already present (in the
                // remaining_forms payload or already generated) — no need to re-run
                // the LLM on existing folders. sanitizeRows normalizes each row's form
                // to its base name and dedupes by form+folder, so the already-generated
                // folders (in `result`) and the newly generated ones for the SAME form
                // collapse into a single group — no stale duplicate row.
                const folderKey = (form, folder) => `${baseFormName(form)}||${(folder || "").trim()}`;
                const payloadKeys = new Set(payload.map((r) => folderKey(r.form_name, r.folder_name)));
                const generatedKeys = new Set(result.map((r) => folderKey(r.form, r.epoch)));
                const uniqueSoaRows = soaRows.filter((r) => {
                  const key = folderKey(r.form_name, r.folder_name);
                  return !payloadKeys.has(key) && !generatedKeys.has(key);
                });

                const combined = [...payload, ...uniqueSoaRows];

                setDataSource(sanitizeRows(result));
                setLoading(false);

                if (combined.length > 0) {
                  processSelectedTemplates(
                    combined,
                    setLoading,
                    setDataSource,
                    setTemplateProgress,
                    setTemplateProgressCompleted
                  );
                } else {
                  setTemplateProgress(100)
                }

              }
            });
          }
        );
      }
    } catch (error) {
      if (error.name === "AbortError") {
        customMessage.warning("Generate request cancelled.");
      } else {
        customMessage.error("Generate Review table failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWriteToWord = async (record) => {
    // __index is the row's position in the flat dataSource, used as the
    // Word page to insert at. Grouping the table for display does not change it.
    const { __index: index, isGroup, key, ...cleanRecord } = record;
    try {
      await insertCRFDataIntoWordAtPage(index, cleanRecord);
      message.success(`Inserted "${cleanRecord.form}" into Word (page ${index + 1})`);
    } catch (err) {
      console.error("Error writing to Word:", err);
      message.error("Failed to write to Word");
    }
  };

  // Form-level insert: write every folder under a form (group row) into Word
  // in one click, reusing the same per-folder page targeting.
  const handleWriteFormToWord = async (record) => {
    const children = record.children || [];
    try {
      for (const child of children) {
        const { __index: index, isGroup, key, ...cleanRecord } = child;
        await insertCRFDataIntoWordAtPage(index, cleanRecord);
      }
      message.success(`Inserted all ${children.length} folder(s) for "${record.form}" into Word`);
    } catch (err) {
      console.error("Error writing form to Word:", err);
      message.error("Failed to write form to Word");
    }
  };

  const columns = [
    {
      title: "Form",
      dataIndex: "form",
      key: "form",
      width: 150,
      ellipsis: true,
      // Parent (group) rows show the form name + folder count.
      // Child rows leave this blank — their folder is shown in the Folder column.
      render: (text, record) => {
        if (!record.isGroup) return null;
        const count = record.children?.length || 0;
        // Form names can carry a trailing note after "~~" (e.g.
        // "FSH Test ~~ [Note c: ...]") — show only the part before "~~".
        const displayName = (text || "").split("~~")[0].trim() || text;
        return (
          <Tooltip title={`${displayName} (${count})`}>
            <span style={{ fontWeight: 600 }}>
              {displayName}{" "}
              <span style={{ color: "#7a93c4", fontWeight: 400 }}>({count})</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Folder",
      dataIndex: "epoch",
      key: "epoch",
      width: 120,
      ellipsis: true,
      render: (text, record) =>
        record.isGroup ? (
          <span style={{ color: "#7a93c4" }}>
            {record.children?.length || 0}{" "}
            folder{(record.children?.length || 0) > 1 ? "s" : ""}
          </span>
        ) : (
          <Tooltip title={text || "-"}>
            <span>{text || "-"}</span>
          </Tooltip>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 60,
      render: (_, record) =>
        record.isGroup ? (
          // Form-level action: insert all folders under this form at once.
          <Tooltip title="Insert all folders for this form into Word">
            <FileWordOutlined
              style={{ color: "#426bba", cursor: "pointer" }}
              onClick={() => handleWriteFormToWord(record)}
            />
          </Tooltip>
        ) : (
          <div>
            <Space size="small">
              <Tooltip title="Click to review fields">
                <EyeOutlined
                  style={{ color: "#426bba", cursor: "pointer" }}
                  onClick={() => handleView(record)}
                />
              </Tooltip>
              <Tooltip title="Click to insert in word">
                <FileWordOutlined
                  style={{ color: "#426bba", cursor: "pointer" }}
                  onClick={() => handleWriteToWord(record)}
                />
              </Tooltip>
            </Space>
          </div>
        ),
    },
  ];

  const handleView = (record) => {
    const { __index: index, isGroup, key, ...cleanRecord } = record;
    if (Office.context.ui) {
      Office.context.ui.displayDialogAsync(
        `${frontEndUrl}/CRFReviewDialog.html`,
        { height: 100, width: 100 },
        (asyncResult) => {
          const dialog = asyncResult.value;
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args) => {
            const data = JSON.parse(args.message);
            console.log("Message Recieved from Child: ", data);
            if (data.action === "ready") {
              setTimeout(() => {
                dialog.messageChild(JSON.stringify({ action: "init", payload: cleanRecord }));
              }, 100);
            }

            if (data.action === "close") {
              // 1. Write updated form to same page
              await insertCRFDataIntoWordAtPage(index, data.payload);
              // 2. Update table data in UI
              setDataSource((prev) => {
                const updated = [...prev];
                updated[index] = data.payload;
                return updated;
              });
              dialog.close();
            }
          });
        }
      );
    }
  };

  const handleBack = () => {
    const isFileListEmpty = fileList.length === 0;

    const modalTitle = isFileListEmpty ? "Are you sure ?" : "Discard Changes?";

    const modalContent = isFileListEmpty
      ? "Do you want to go back?"
      : "Your file will be discarded if you go back. Are you sure?";

    Modal.confirm({
      title: modalTitle,
      content: modalContent,
      okText: "Yes, Go Back",
      cancelText: "Stay",
      okType: "danger",
      onOk() {
        if (dataSource?.length === 0) {
          onBack();
        }
        abortControllerHandle();
        clearIntervalHandle();
        setProtocolId(null);
        setIsFileDigitized(false);
        setFileList([]);
        setDataSource([]);
      },
      onCancel() {
        console.log("Stay on page");
      },
    });
  };

  const handleDeleteProtocol = async (record) => {
    try {
      await deleteProtocol({ protocol_id: record.protocol_id });
      message.success("Protocol deleted successfully.");
      fetchProtocolVersions(); // refresh the list
    } catch (err) {
      console.error("Error deleting protocol:", err);
      message.error("Failed to delete protocol.");
    }
  };

  const fetchProtocolVersions = () => {
    setLoadingVersion(true)
    getAllProtocolVersion({ user_id: user.username })
      .then(protocols => setProtocols(protocols))
      .catch(err => console.log("Error while fetching protocol versions: ", err))
      .finally(() => setLoadingVersion(false))
  }

  useEffect(() => {
    fetchProtocolVersions()
  }, [])

  const crfContextValue = {
    loading,
    setLoading,
    setFileList,
    fileList,
    handleBase64Upload,
    dataSource,
    columns,
    loading,
    onClickGenerate,
    handleBack,
    onBack,
    tip,
    setTip,
    isFileDigitized,
    setIsFileDigitized,
    progress,
    protocolId,
    setProtocolId,
    templateProgress,
    templateProgressCompleted,
    totalTemplate,
    templateLoading,
    protocols,
    onClickSpecificVersion,
    loadingVersion,
    fetchProtocolVersions,
    onDeleteSession: handleDeleteProtocol,
  };

  return (
    <CRFContext.Provider value={crfContextValue}>
      <UploadSourceDocument />
    </CRFContext.Provider>
  );
};

export default CRF;

Office.onReady().then(() => {
  const root = document.getElementById("CRF_container");
  if (root) {
    console.log("Inside if");
    createRoot(root).render(<CRF />);
  } else {
    console.log("Root element not found");
  }
});
